import { ClientDisplayableError, isClientDisplayableError, isErrorResponse, ValetTokenOperation, } from '@standardnotes/responses';
import { FileItem, FileProtocolV1Constants, FillItemContentSpecialized, EncryptedPayload, isEncryptedPayload, DecryptedPayload, FillItemContent, PayloadVaultOverrides, PayloadTimestampDefaults, CreateItemFromPayload, } from '@standardnotes/models';
import { UuidGenerator } from '@standardnotes/utils';
import { c } from 'ttag';
import { SNItemsKey } from '@standardnotes/encryption';
import { DownloadAndDecryptFileOperation, EncryptAndUploadFileOperation, FileDecryptor, readAndDecryptBackupFileUsingFileSystemAPI, FileBackupsConstantsV1, OrderedByteChunker, FileMemoryCache, readAndDecryptBackupFileUsingBackupService, } from '@standardnotes/files';
import { ButtonType } from '../Alert/AlertService';
import { AbstractService } from '../Service/AbstractService';
import { DecryptItemsKeyWithUserFallback } from '../Encryption/Functions';
import { SharedVaultServer } from '@standardnotes/api';
import { ContentType } from '@standardnotes/domain-core';
const OneHundredMb = 100 * 1000000;
export class FileService extends AbstractService {
    constructor(api, mutator, sync, encryptor, challengor, http, alertService, crypto, internalEventBus, logger, backupsService) {
        super(internalEventBus);
        this.api = api;
        this.mutator = mutator;
        this.sync = sync;
        this.encryptor = encryptor;
        this.challengor = challengor;
        this.alertService = alertService;
        this.crypto = crypto;
        this.internalEventBus = internalEventBus;
        this.logger = logger;
        this.backupsService = backupsService;
        this.encryptedCache = new FileMemoryCache(OneHundredMb);
        this.sharedVault = new SharedVaultServer(http);
    }
    deinit() {
        super.deinit();
        this.encryptedCache.clear();
        this.encryptedCache = undefined;
        this.api = undefined;
        this.encryptor = undefined;
        this.sync = undefined;
        this.alertService = undefined;
        this.challengor = undefined;
        this.crypto = undefined;
    }
    minimumChunkSize() {
        return 5000000;
    }
    async createUserValetToken(remoteIdentifier, operation, unencryptedFileSizeForUpload) {
        return this.api.createUserFileValetToken(remoteIdentifier, operation, unencryptedFileSizeForUpload);
    }
    async createSharedVaultValetToken(params) {
        if (params.operation !== ValetTokenOperation.Write && !params.fileUuidRequiredForExistingFiles) {
            throw new Error('File UUID is required for for non-write operations');
        }
        const valetTokenResponse = await this.sharedVault.createSharedVaultFileValetToken({
            sharedVaultUuid: params.sharedVaultUuid,
            sharedVaultOwnerUuid: params.sharedVaultOwnerUuid,
            fileUuid: params.fileUuidRequiredForExistingFiles,
            remoteIdentifier: params.remoteIdentifier,
            operation: params.operation,
            unencryptedFileSize: params.unencryptedFileSizeForUpload,
            moveOperationType: params.moveOperationType,
            sharedVaultToSharedVaultMoveTargetUuid: params.sharedVaultToSharedVaultMoveTargetUuid,
        });
        if (isErrorResponse(valetTokenResponse)) {
            return new ClientDisplayableError(c('B7.FilesSubscriptionHelp.Files.Error').t `Could not create valet token`);
        }
        return valetTokenResponse.data.valetToken;
    }
    async moveFileToSharedVault(file, sharedVault) {
        const valetTokenResult = await this.createSharedVaultValetToken({
            sharedVaultUuid: file.shared_vault_uuid ? file.shared_vault_uuid : sharedVault.sharing.sharedVaultUuid,
            sharedVaultOwnerUuid: sharedVault.sharing.ownerUserUuid,
            remoteIdentifier: file.remoteIdentifier,
            operation: ValetTokenOperation.Move,
            fileUuidRequiredForExistingFiles: file.uuid,
            moveOperationType: file.shared_vault_uuid ? 'shared-vault-to-shared-vault' : 'user-to-shared-vault',
            sharedVaultToSharedVaultMoveTargetUuid: file.shared_vault_uuid ? sharedVault.sharing.sharedVaultUuid : undefined,
        });
        if (isClientDisplayableError(valetTokenResult)) {
            return valetTokenResult;
        }
        const moveResult = await this.api.moveFile(valetTokenResult);
        if (!moveResult) {
            return new ClientDisplayableError(c('B7.FilesSubscriptionHelp.Files.Error').t `Could not move file`);
        }
    }
    async moveFileOutOfSharedVault(file) {
        if (!file.shared_vault_uuid) {
            return new ClientDisplayableError(c('B7.FilesSubscriptionHelp.Files.Error').t `File is not in a shared vault`);
        }
        const valetTokenResult = await this.createSharedVaultValetToken({
            sharedVaultUuid: file.shared_vault_uuid,
            remoteIdentifier: file.remoteIdentifier,
            operation: ValetTokenOperation.Move,
            fileUuidRequiredForExistingFiles: file.uuid,
            moveOperationType: 'shared-vault-to-user',
        });
        if (isClientDisplayableError(valetTokenResult)) {
            return valetTokenResult;
        }
        const moveResult = await this.api.moveFile(valetTokenResult);
        if (!moveResult) {
            return new ClientDisplayableError(c('B7.FilesSubscriptionHelp.Files.Error').t `Could not move file`);
        }
    }
    async beginNewFileUpload(sizeInBytes, vault) {
        const remoteIdentifier = UuidGenerator.GenerateUuid();
        const valetTokenResult = vault && vault.isSharedVaultListing()
            ? await this.createSharedVaultValetToken({
                sharedVaultUuid: vault.sharing.sharedVaultUuid,
                sharedVaultOwnerUuid: vault.sharing.ownerUserUuid,
                remoteIdentifier,
                operation: ValetTokenOperation.Write,
                unencryptedFileSizeForUpload: sizeInBytes,
            })
            : await this.createUserValetToken(remoteIdentifier, ValetTokenOperation.Write, sizeInBytes);
        if (valetTokenResult instanceof ClientDisplayableError) {
            return valetTokenResult;
        }
        const key = this.crypto.generateRandomKey(FileProtocolV1Constants.KeySize);
        const fileParams = {
            key,
            remoteIdentifier,
            decryptedSize: sizeInBytes,
        };
        const uploadOperation = new EncryptAndUploadFileOperation(fileParams, valetTokenResult, this.crypto, this.api, vault);
        const uploadSessionStarted = await this.api.startUploadSession(valetTokenResult, vault && vault.isSharedVaultListing() ? 'shared-vault' : 'user');
        if (isErrorResponse(uploadSessionStarted)) {
            return ClientDisplayableError.FromNetworkError(uploadSessionStarted);
        }
        if (!uploadSessionStarted.data.uploadId) {
            return new ClientDisplayableError(c('B7.FilesSubscriptionHelp.Files.Error').t `Could not start upload session`);
        }
        return uploadOperation;
    }
    async pushBytesForUpload(operation, bytes, chunkId, isFinalChunk) {
        const success = await operation.pushBytes(bytes, chunkId, isFinalChunk);
        if (!success) {
            return new ClientDisplayableError(c('B7.FilesSubscriptionHelp.Files.Error').t `Failed to push file bytes to server`);
        }
        return undefined;
    }
    async finishUpload(operation, fileMetadata, uuid) {
        const uploadSessionClosed = await this.api.closeUploadSession(operation.getValetToken(), operation.vault && operation.vault.isSharedVaultListing() ? 'shared-vault' : 'user');
        if (uploadSessionClosed instanceof ClientDisplayableError) {
            return uploadSessionClosed;
        }
        if (!uploadSessionClosed) {
            return new ClientDisplayableError(c('B7.FilesSubscriptionHelp.Files.Error').t `Could not close upload session`);
        }
        const result = operation.getResult();
        const fileContent = {
            decryptedSize: result.finalDecryptedSize,
            encryptedChunkSizes: operation.encryptedChunkSizes,
            encryptionHeader: result.encryptionHeader,
            key: result.key,
            mimeType: fileMetadata.mimeType,
            name: fileMetadata.name,
            remoteIdentifier: result.remoteIdentifier,
        };
        const filePayload = new DecryptedPayload({
            uuid,
            content_type: ContentType.TYPES.File,
            content: FillItemContent(FillItemContentSpecialized(fileContent)),
            dirty: true,
            ...PayloadVaultOverrides(operation.vault),
            ...PayloadTimestampDefaults(),
        });
        const fileItem = CreateItemFromPayload(filePayload);
        const insertedItem = await this.mutator.insertItem(fileItem);
        await this.sync.sync();
        return insertedItem;
    }
    async decryptCachedEntry(file, entry) {
        const decryptOperation = new FileDecryptor(file, this.crypto);
        let decryptedAggregate = new Uint8Array();
        const orderedChunker = new OrderedByteChunker(file.encryptedChunkSizes, 'memcache', async (chunk) => {
            const decryptedBytes = decryptOperation.decryptBytes(chunk.data);
            if (decryptedBytes) {
                decryptedAggregate = new Uint8Array([...decryptedAggregate, ...decryptedBytes.decryptedBytes]);
            }
        });
        await orderedChunker.addBytes(entry.encryptedBytes);
        return { decryptedBytes: decryptedAggregate };
    }
    async downloadFile(file, onDecryptedBytes) {
        var _a;
        const cachedBytes = this.encryptedCache.get(file.uuid);
        if (cachedBytes) {
            const decryptedBytes = await this.decryptCachedEntry(file, cachedBytes);
            await onDecryptedBytes(decryptedBytes.decryptedBytes, {
                encryptedFileSize: cachedBytes.encryptedBytes.length,
                encryptedBytesDownloaded: cachedBytes.encryptedBytes.length,
                encryptedBytesRemaining: 0,
                percentComplete: 100,
                source: 'memcache',
            });
            return undefined;
        }
        const fileBackup = await ((_a = this.backupsService) === null || _a === void 0 ? void 0 : _a.getFileBackupInfo(file));
        if (this.backupsService && fileBackup) {
            this.logger.info('Downloading file from backup', fileBackup);
            await readAndDecryptBackupFileUsingBackupService(file, this.backupsService, this.crypto, async (chunk) => {
                this.logger.info('Got local file chunk', chunk.progress);
                return onDecryptedBytes(chunk.data, chunk.progress);
            });
            this.logger.info('Finished downloading file from backup');
            return undefined;
        }
        else {
            this.logger.info('Downloading file from network');
            const addToCache = file.encryptedSize < this.encryptedCache.maxSize;
            let cacheEntryAggregate = new Uint8Array();
            const tokenResult = file.shared_vault_uuid
                ? await this.createSharedVaultValetToken({
                    sharedVaultUuid: file.shared_vault_uuid,
                    remoteIdentifier: file.remoteIdentifier,
                    operation: ValetTokenOperation.Read,
                    fileUuidRequiredForExistingFiles: file.uuid,
                })
                : await this.createUserValetToken(file.remoteIdentifier, ValetTokenOperation.Read);
            if (tokenResult instanceof ClientDisplayableError) {
                return tokenResult;
            }
            const operation = new DownloadAndDecryptFileOperation(file, this.crypto, this.api, tokenResult);
            const result = await operation.run(async ({ decrypted, encrypted, progress }) => {
                if (addToCache) {
                    cacheEntryAggregate = new Uint8Array([...cacheEntryAggregate, ...encrypted.encryptedBytes]);
                }
                return onDecryptedBytes(decrypted.decryptedBytes, progress);
            });
            if (addToCache && cacheEntryAggregate.byteLength > 0) {
                this.encryptedCache.add(file.uuid, { encryptedBytes: cacheEntryAggregate });
            }
            return result.error;
        }
    }
    async deleteFile(file) {
        this.encryptedCache.remove(file.uuid);
        const tokenResult = file.shared_vault_uuid
            ? await this.createSharedVaultValetToken({
                sharedVaultUuid: file.shared_vault_uuid,
                remoteIdentifier: file.remoteIdentifier,
                operation: ValetTokenOperation.Delete,
                fileUuidRequiredForExistingFiles: file.uuid,
            })
            : await this.createUserValetToken(file.remoteIdentifier, ValetTokenOperation.Delete);
        if (tokenResult instanceof ClientDisplayableError) {
            return tokenResult;
        }
        const result = await this.api.deleteFile(tokenResult, file.shared_vault_uuid ? 'shared-vault' : 'user');
        if (isErrorResponse(result)) {
            const deleteAnyway = await this.alertService.confirm(c('B7.FilesSubscriptionHelp.Files.Confirmation')
                .t `This file could not be deleted from the server, possibly because you are attempting to delete a file item that was imported from another account. Would you like to remove this file item from your account anyway? If you're sure the file is yours and still exists on the server, do not choose this option, and instead try to delete it again.`, c('B7.FilesSubscriptionHelp.Files.Confirmation').t `Unable to Delete`, c('B7.FilesSubscriptionHelp.Files.Confirmation').t `Delete Anyway`, ButtonType.Danger);
            if (!deleteAnyway) {
                return ClientDisplayableError.FromNetworkError(result);
            }
        }
        await this.mutator.setItemToBeDeleted(file);
        await this.sync.sync();
        return undefined;
    }
    isFileNameFileBackupRelated(name) {
        if (name === FileBackupsConstantsV1.MetadataFileName) {
            return 'metadata';
        }
        else if (name === FileBackupsConstantsV1.BinaryFileName) {
            return 'binary';
        }
        return false;
    }
    async decryptBackupMetadataFile(metdataFile) {
        const encryptedItemsKey = new EncryptedPayload({
            ...metdataFile.itemsKey,
            waitingForKey: false,
            errorDecrypting: false,
        });
        const decryptedItemsKeyResult = await DecryptItemsKeyWithUserFallback(encryptedItemsKey, this.encryptor, this.challengor);
        if (decryptedItemsKeyResult === 'failed' || decryptedItemsKeyResult === 'aborted') {
            return undefined;
        }
        const encryptedFile = new EncryptedPayload({ ...metdataFile.file, waitingForKey: false, errorDecrypting: false });
        const itemsKey = new SNItemsKey(decryptedItemsKeyResult);
        const decryptedFile = await this.encryptor.decryptSplitSingle({
            usesItemsKey: {
                items: [encryptedFile],
                key: itemsKey,
            },
        });
        if (isEncryptedPayload(decryptedFile)) {
            return undefined;
        }
        return new FileItem(decryptedFile);
    }
    async selectFile(fileSystem) {
        const result = await fileSystem.selectFile();
        return result;
    }
    async readBackupFileAndSaveDecrypted(fileHandle, file, fileSystem) {
        const destinationDirectoryHandle = await fileSystem.selectDirectory();
        if (destinationDirectoryHandle === 'aborted' || destinationDirectoryHandle === 'failed') {
            return destinationDirectoryHandle;
        }
        const destinationFileHandle = await fileSystem.createFile(destinationDirectoryHandle, file.name);
        if (destinationFileHandle === 'aborted' || destinationFileHandle === 'failed') {
            return destinationFileHandle;
        }
        const result = await readAndDecryptBackupFileUsingFileSystemAPI(fileHandle, file, fileSystem, this.crypto, async (decryptedBytes) => {
            await fileSystem.saveBytes(destinationFileHandle, decryptedBytes);
        });
        await fileSystem.closeFileWriteStream(destinationFileHandle);
        return result;
    }
    async readBackupFileBytesDecrypted(fileHandle, file, fileSystem) {
        let bytes = new Uint8Array();
        await readAndDecryptBackupFileUsingFileSystemAPI(fileHandle, file, fileSystem, this.crypto, async (decryptedBytes) => {
            bytes = new Uint8Array([...bytes, ...decryptedBytes]);
        });
        return bytes;
    }
}
